import { useEffect, useRef, useCallback, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, QrCode, Copy, Share2, ScanLine } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const W = 1080, H = 1620; // base poster 2:3

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};
const wrap = (ctx, text, x, y, maxW, lh) => {
  const words = String(text).split(" ");
  let line = "", yy = y;
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line, x, yy); line = w; yy += lh; }
    else line = test;
  }
  ctx.fillText(line, x, yy);
  return yy;
};

const THEMES = {
  suffle: { label: "Suffle", bg: ["#d946ef", "#7c3aed", "#1e1b4b"], band: ["#ec4899", "#8b5cf6"], sub: "#f0abfc", strip: "#e9d5ff", cat: "#e9d5ff", trust: "#c4b5fd" },
  midnight: { label: "Midnight", bg: ["#312e81", "#1e1b4b", "#020617"], band: ["#6366f1", "#0ea5e9"], sub: "#a5b4fc", strip: "#c7d2fe", cat: "#c7d2fe", trust: "#94a3b8" },
};
const SIZES = { poster: { label: "Poster (2:3)", w: 1080, h: 1620 }, a4: { label: "A4", w: 1240, h: 1754 }, a5: { label: "A5", w: 874, h: 1240 } };
const TEMPLATES = { classic: "Classic", minimal: "Minimal", festive: "Festive" };

const fmtDay = (day) => { try { return new Date(day + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); } catch { return day; } };
const Sparkline = ({ data }) => {
  const [hi, setHi] = useState(null);
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 100, h = 26, step = w / Math.max(1, data.length - 1);
  const pt = (d, i) => ({ x: i * step, y: h - (d.count / max) * (h - 4) - 2 });
  const pts = data.map((d, i) => { const p = pt(d, i); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
  return (
    <span className="relative inline-block">
      <svg width={w} height={h} className="overflow-visible">
        <polyline points={pts} fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const p = pt(d, i);
          const on = hi && hi.i === i;
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={on ? 3 : 1.8} fill={on ? "#047857" : "#059669"} />
              <circle cx={p.x} cy={p.y} r="8" fill="transparent" style={{ cursor: "pointer" }}
                onMouseEnter={() => setHi({ i, ...p, ...d })} onMouseLeave={() => setHi(null)}
                data-testid={`sparkline-point-${i}`} />
            </g>
          );
        })}
      </svg>
      {hi && (
        <span className="pointer-events-none absolute z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white shadow-lg"
          style={{ left: hi.x, top: -28 }} data-testid="sparkline-tooltip">
          {fmtDay(hi.day)}: {hi.count} scan{hi.count === 1 ? "" : "s"}
        </span>
      )}
    </span>
  );
};

const TemplateThumb = ({ kind, T }) => {
  const cardY = kind === "festive" ? 13 : 8, qrY = kind === "festive" ? 24 : 20;
  return (
    <svg width="40" height="58" viewBox="0 0 40 58" aria-hidden="true">
      <defs><linearGradient id={`tg-${kind}`} x1="0" y1="0" x2="0.4" y2="1">
        <stop offset="0" stopColor={T.bg[0]} /><stop offset="0.5" stopColor={T.bg[1]} /><stop offset="1" stopColor={T.bg[2]} />
      </linearGradient></defs>
      <rect width="40" height="58" rx="4" fill={`url(#tg-${kind})`} />
      {kind === "festive" && <>
        <rect x="6" y="4" width="28" height="5" rx="2.5" fill="#fde047" opacity="0.9" />
        <circle cx="6" cy="17" r="1.4" fill="#f472b6" /><circle cx="34" cy="15" r="1.4" fill="#34d399" /><circle cx="8" cy="44" r="1.4" fill="#60a5fa" />
      </>}
      <rect x="9" y={cardY} width="22" height="7" rx="2" fill="#ffffff" opacity="0.9" />
      <rect x="11" y={qrY} width="18" height="18" rx="2" fill="#ffffff" />
      <g fill={T.bg[2]}>{[0, 1, 2, 3].map((r) => [0, 1, 2, 3].map((c) => (r + c) % 2 === 0 && <rect key={`${r}-${c}`} x={13 + c * 4} y={qrY + 2 + r * 4} width="2.5" height="2.5" />))}</g>
      <rect x="11" y={qrY + 21} width="18" height="4" rx="2" fill={T.band[0]} />
      {kind !== "minimal" && <>
        <rect x="9" y="51" width="22" height="1.6" rx="0.8" fill="#ffffff" opacity="0.7" />
        <rect x="12" y="54" width="16" height="1.6" rx="0.8" fill="#ffffff" opacity="0.5" />
      </>}
    </svg>
  );
};

const analyzeLogo = (img) => {
  try {
    const n = 24, c = document.createElement("canvas"); c.width = n; c.height = n;
    const x = c.getContext("2d"); x.drawImage(img, 0, 0, n, n);
    const d = x.getImageData(0, 0, n, n).data, lum = [];
    let sum = 0, sum2 = 0, edges = 0;
    for (let i = 0; i < d.length; i += 4) { const L = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]; lum.push(L); sum += L; sum2 += L * L; }
    const np = lum.length, mean = sum / np, varc = sum2 / np - mean * mean;
    for (let r = 0; r < n; r++) for (let col = 1; col < n; col++) if (Math.abs(lum[r * n + col] - lum[r * n + col - 1]) > 40) edges++;
    const edgeRatio = edges / (n * (n - 1));
    return { busy: varc > 3000 || edgeRatio > 0.18 };
  } catch { return { busy: false }; }
};

const hex = (h) => h.replace("#", "");
const mix = (a, b, t) => {
  const pa = [0, 2, 4].map((i) => parseInt(hex(a).slice(i, i + 2), 16));
  const pb = [0, 2, 4].map((i) => parseInt(hex(b).slice(i, i + 2), 16));
  return "#" + pa.map((c, i) => Math.round(c + (pb[i] - c) * t).toString(16).padStart(2, "0")).join("");
};

export default function StoreQRPoster({ storeName, storeUrl, slug, logo, accent = "#7c3aed" }) {
  const posterRef = useRef(null);
  const qrRef = useRef(null);
  const logoRef = useRef(null);
  const [theme, setTheme] = useState("suffle");
  const [template, setTemplate] = useState("classic");
  const [size, setSize] = useState("poster");
  const [bleed, setBleed] = useState(false);
  const [stats, setStats] = useState(null);

  const themes = { ...THEMES, accent: { label: "My accent", bg: [mix(accent, "#ffffff", 0.15), accent, mix(accent, "#000000", 0.55)], band: [accent, mix(accent, "#ffffff", 0.35)], sub: mix(accent, "#ffffff", 0.55), strip: mix(accent, "#ffffff", 0.6), cat: mix(accent, "#ffffff", 0.6), trust: mix(accent, "#ffffff", 0.45) } };
  const trackUrl = slug ? `${window.location.origin}/api/marketplace/qr/${slug}` : storeUrl;

  useEffect(() => { api.get("/marketplace/storefront/qr-stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const draw = useCallback(() => {
    const canvas = posterRef.current;
    const qrCanvas = qrRef.current?.querySelector("canvas");
    if (!canvas || !qrCanvas) return;
    const ctx = canvas.getContext("2d");
    const T = themes[theme] || THEMES.suffle;
    ctx.clearRect(0, 0, W, H);
    const g = ctx.createLinearGradient(0, 0, W * 0.4, H);
    g.addColorStop(0, T.bg[0]); g.addColorStop(0.45, T.bg[1]); g.addColorStop(1, T.bg[2]);
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    if (template === "festive") {
      [[90, 300, "#fde047"], [990, 360, "#f472b6"], [120, 900, "#34d399"], [960, 980, "#60a5fa"], [80, 1200, "#f472b6"], [1000, 1240, "#fde047"]].forEach(([dx, dy, c]) => { ctx.fillStyle = c; ctx.beginPath(); ctx.arc(dx, dy, 14, 0, 7); ctx.fill(); });
      ctx.textAlign = "center"; ctx.fillStyle = "#fde047"; ctx.font = "700 26px Arial"; ctx.fillText("✦  SPECIAL OFFERS INSIDE  ✦", W / 2, 148);
    }
    ctx.textAlign = "center";

    ctx.fillStyle = "#ffffff"; ctx.font = "600 30px Arial"; ctx.fillText("SUFFLE ONLINE", W / 2, 78);
    ctx.font = "italic 22px Georgia"; ctx.fillStyle = T.sub; ctx.fillText("Shop. Discover. Suffle.", W / 2, 112);
    ctx.fillStyle = "#ffffff"; ctx.font = "800 58px Arial";
    const endY = wrap(ctx, storeName || "Your Store", W / 2, 190, W - 140, 66);

    const pillW = 300, pillY = endY + 26;
    const pg = ctx.createLinearGradient(W / 2 - pillW / 2, 0, W / 2 + pillW / 2, 0);
    pg.addColorStop(0, T.band[0]); pg.addColorStop(1, T.band[1]);
    ctx.fillStyle = pg; rr(ctx, W / 2 - pillW / 2, pillY, pillW, 46, 23); ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.font = "700 22px Arial"; ctx.fillText("OFFICIAL STORE", W / 2, pillY + 31);
    ctx.font = "600 24px Arial"; ctx.fillStyle = T.strip; ctx.fillText("SHOP  •  BOOK  •  CONNECT  •  GET IT DONE", W / 2, pillY + 96);

    const cardW = 620, cardX = (W - cardW) / 2, cardY = pillY + 130, cardH = 700;
    ctx.fillStyle = "#ffffff"; rr(ctx, cardX, cardY, cardW, cardH, 36); ctx.fill();
    const qs = 480, qx = W / 2 - qs / 2, qy = cardY + 46;
    ctx.drawImage(qrCanvas, qx, qy, qs, qs);
    const chip = 96, cx = W / 2 - chip / 2, cy = qy + qs / 2 - chip / 2;
    const lg = logoRef.current;
    const hasLogo = lg && lg.complete && lg.naturalWidth;
    const info = hasLogo ? analyzeLogo(lg) : { busy: false };
    const pad = info.busy ? 14 : 6; // busy/low-contrast logo → wider white quiet-zone
    ctx.fillStyle = "#ffffff"; rr(ctx, cx - pad, cy - pad, chip + pad * 2, chip + pad * 2, 20); ctx.fill();
    if (info.busy) {
      ctx.fillStyle = "#ffffff"; rr(ctx, cx, cy, chip, chip, 16); ctx.fill(); // solid backing keeps QR scannable
    } else {
      const cg = ctx.createLinearGradient(cx, cy, cx + chip, cy + chip);
      cg.addColorStop(0, T.band[1]); cg.addColorStop(1, T.band[0]);
      ctx.fillStyle = cg; rr(ctx, cx, cy, chip, chip, 16); ctx.fill();
    }
    if (hasLogo) {
      ctx.save(); rr(ctx, cx, cy, chip, chip, 16); ctx.clip(); ctx.drawImage(lg, cx, cy, chip, chip); ctx.restore();
      ctx.strokeStyle = info.busy ? "rgba(15,23,42,0.15)" : "rgba(255,255,255,0.9)"; ctx.lineWidth = 3; rr(ctx, cx, cy, chip, chip, 16); ctx.stroke();
    } else {
      ctx.fillStyle = info.busy ? T.band[0] : "#ffffff"; ctx.font = "800 60px Georgia"; ctx.fillText("S", W / 2, cy + 68);
    }

    const bandY = qy + qs + 24, bandW = cardW - 80;
    const bg = ctx.createLinearGradient(0, bandY, bandW, bandY);
    bg.addColorStop(0, T.band[0]); bg.addColorStop(1, T.band[1]);
    ctx.fillStyle = bg; rr(ctx, W / 2 - bandW / 2, bandY, bandW, 100, 20); ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.font = "800 40px Arial"; ctx.fillText("SCAN HERE", W / 2, bandY + 50);
    ctx.font = "500 22px Arial"; ctx.fillText("Visit our store on Suffle Online", W / 2, bandY + 82);

    ctx.fillStyle = T.cat; ctx.font = "600 24px Arial";
    if (template === "minimal") {
      ctx.fillText("Shop products & book services — all in one place.", W / 2, cardY + cardH + 90);
    } else {
      ctx.fillText("Shop Products   ·   Explore Services   ·   Grab Offers & Deals", W / 2, cardY + cardH + 60);
      ctx.fillStyle = "#ffffff"; ctx.font = "italic 700 44px Georgia";
      ctx.fillText("Your Needs. Our Marketplace.", W / 2, cardY + cardH + 130);
      ctx.fillStyle = T.cat; ctx.font = "500 22px Arial";
      ctx.fillText("Fashion · Electronics · Home & Living · Food · Health", W / 2, cardY + cardH + 186);
      ctx.fillText("Services · Education · and more", W / 2, cardY + cardH + 218);
      const tbY = H - 180;
      ctx.fillStyle = "rgba(15,10,40,0.55)"; ctx.fillRect(0, tbY, W, 120);
      ctx.fillStyle = "#ffffff"; ctx.font = "600 22px Arial";
      ctx.fillText("Support Local  ·  ✓ Verified Sellers & Providers  ·  Trusted Services", W / 2, tbY + 46);
      ctx.fillStyle = T.trust; ctx.font = "700 20px Arial";
      ctx.fillText("TOGETHER FOR A BETTER TOMORROW", W / 2, tbY + 84);
    }

    ctx.fillStyle = "#0b0a1f"; ctx.fillRect(0, H - 60, W, 60);
    ctx.fillStyle = "#ffffff"; ctx.font = "600 22px Arial";
    ctx.fillText((storeUrl || "").replace(/^https?:\/\//, "") + "   |   Shop · Book · Get Services", W / 2, H - 22);

    const img = logoRef.current;
    if (img && img.complete && img.naturalWidth) {
      const ls = 64; ctx.save(); rr(ctx, 40, 40, ls, ls, 14); ctx.clip();
      ctx.drawImage(img, 40, 40, ls, ls); ctx.restore();
    }
  }, [storeName, storeUrl, logo, theme, accent, template]); // eslint-disable-line

  useEffect(() => { const t = setTimeout(draw, 120); return () => clearTimeout(t); }, [draw]);

  const exportCanvas = () => {
    draw();
    const S = SIZES[size];
    if (size === "poster") return posterRef.current;
    const T = themes[theme] || THEMES.suffle;
    const bp = bleed ? 45 : 0; // ~3mm at 150dpi
    const out = document.createElement("canvas"); out.width = S.w + bp * 2; out.height = S.h + bp * 2;
    const ctx = out.getContext("2d");
    ctx.fillStyle = T.bg[2]; ctx.fillRect(0, 0, out.width, out.height);
    const scale = S.w / W, dh = H * scale, dy = bp + (S.h - dh) / 2;
    ctx.drawImage(posterRef.current, bp, dy, S.w, dh);
    if (bleed) {
      ctx.strokeStyle = "#000000"; ctx.lineWidth = 2; const L = 30;
      [[bp, bp, 1, 1], [out.width - bp, bp, -1, 1], [bp, out.height - bp, 1, -1], [out.width - bp, out.height - bp, -1, -1]].forEach(([x, y, sx, sy]) => {
        ctx.beginPath(); ctx.moveTo(x - sx * L, y); ctx.lineTo(x, y); ctx.moveTo(x, y - sy * L); ctx.lineTo(x, y); ctx.stroke();
      });
    }
    return out;
  };
  const downloadJPG = () => {
    const c = exportCanvas();
    const a = document.createElement("a");
    a.href = c.toDataURL("image/jpeg", 0.92);
    a.download = size === "poster" ? "suffle-store-poster.jpg" : `suffle-store-poster-${size}.jpg`; a.click();
    toast.success("Poster downloaded");
  };
  const downloadQR = () => {
    const qrCanvas = qrRef.current?.querySelector("canvas");
    if (!qrCanvas) return;
    const a = document.createElement("a");
    a.href = qrCanvas.toDataURL("image/png"); a.download = "suffle-store-qr.png"; a.click();
    toast.success("QR downloaded");
  };
  const caption = `Visit ${storeName || "our store"} on Suffle Online: ${storeUrl}`;
  const copyLink = async () => { try { await navigator.clipboard.writeText(storeUrl); toast.success("Store link copied"); } catch { toast.error("Couldn't copy — long-press the link to copy it."); } };
  const share = async () => { if (navigator.share) { try { await navigator.share({ title: storeName, text: caption, url: storeUrl }); } catch { /* cancelled */ } } else copyLink(); };
  const btn = "inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-blue-300";
  const chip = (active) => `rounded-full px-3 py-1.5 text-xs font-semibold ${active ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`;

  return (
    <div data-testid="store-qr-poster">
      <div ref={qrRef} className="hidden"><QRCodeCanvas value={trackUrl} size={480} level="H" includeMargin /></div>
      {logo && <img ref={logoRef} src={logo} alt="" crossOrigin="anonymous" className="hidden" onLoad={draw} />}
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <canvas ref={posterRef} width={W} height={H} data-testid="poster-canvas" className="w-full max-w-[300px] rounded-xl border border-slate-200 shadow-sm" />
        <div className="flex-1 space-y-3">
          <div>
            <p className="mb-1 text-xs font-semibold text-slate-500">Theme</p>
            <div className="flex flex-wrap gap-2" data-testid="poster-themes">
              {Object.entries(themes).map(([k, t]) => <button key={k} onClick={() => setTheme(k)} data-testid={`poster-theme-${k}`} className={chip(theme === k)}>{t.label}</button>)}
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-slate-500">Layout</p>
            <div className="flex flex-wrap gap-2" data-testid="poster-templates">
              {Object.entries(TEMPLATES).map(([k, l]) => (
                <button key={k} onClick={() => setTemplate(k)} data-testid={`poster-template-${k}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 p-1.5 transition ${template === k ? "border-slate-900" : "border-slate-200 hover:border-slate-300"}`}>
                  <span className="overflow-hidden rounded-[5px] shadow-sm"><TemplateThumb kind={k} T={themes[theme] || THEMES.suffle} /></span>
                  <span className={`text-[11px] font-semibold ${template === k ? "text-slate-900" : "text-slate-500"}`}>{l}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold text-slate-500">Download size</p>
            <div className="flex flex-wrap gap-2" data-testid="poster-sizes">
              {Object.entries(SIZES).map(([k, s]) => <button key={k} onClick={() => setSize(k)} data-testid={`poster-size-${k}`} className={chip(size === k)}>{s.label}</button>)}
            </div>
            {size !== "poster" && <button onClick={() => setBleed((b) => !b)} data-testid="poster-bleed" className={`mt-2 ${chip(bleed)}`}>{bleed ? "✓ " : ""}Crop marks + bleed</button>}
          </div>
          <button onClick={downloadJPG} data-testid="poster-download-jpg" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><Download className="h-4 w-4" /> Download poster (JPG)</button>
          <div className="flex flex-wrap gap-2">
            <button onClick={copyLink} data-testid="poster-copy" className={btn}><Copy className="h-3.5 w-3.5" /> Copy link</button>
            <button onClick={share} data-testid="poster-share" className={btn}><Share2 className="h-3.5 w-3.5" /> Share</button>
            <button onClick={downloadQR} data-testid="poster-download-qr" className={btn}><QrCode className="h-3.5 w-3.5" /> Plain QR (PNG)</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`https://wa.me/?text=${encodeURIComponent(caption)}`} target="_blank" rel="noreferrer noopener noreferrer" data-testid="poster-wa" className={btn}>WhatsApp</a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl)}`} target="_blank" rel="noreferrer noopener noreferrer" data-testid="poster-fb" className={btn}>Facebook</a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`} target="_blank" rel="noreferrer noopener noreferrer" data-testid="poster-x" className={btn}>X</a>
          </div>
          {stats && <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 px-2.5 py-1.5" data-testid="poster-scans">
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><ScanLine className="h-3.5 w-3.5" /> {stats.scans} QR scan{stats.scans === 1 ? "" : "s"} total</p>
            {stats.series && <span className="inline-flex items-center gap-1.5" data-testid="poster-sparkline"><Sparkline data={stats.series} /><span className="text-[10px] font-medium text-emerald-600">last 7 days</span></span>}
          </div>}
          <p className="text-xs text-slate-400">The QR opens your store page: <span className="font-medium text-slate-500">{storeUrl}</span></p>
        </div>
      </div>
    </div>
  );
}
